import { ColumnsType, TableProps } from "antd/es/table";
import {
  AnalyticsPageName,
  BacktestingActivity,
  LanguageNameSpaces,
} from "@shared/types";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { formatDate } from "@shared/utils/date";
import ChangeTag from "common/Tags/ChangeTag";
import Table from "common/Table/Table";
import React, { useMemo, useState } from "react";
import { typography } from "@shared/common/Themes/typography";
import styled, { useTheme } from "styled-components";
import { TableLoadingEmpty } from "common/styles";
import EmptyView from "common/Empty/EmptyView";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { getPairFromSymbol } from "@shared/utils/misc";
import { getLocaleStringWithPrecision, toFixed } from "@shared/utils/numbers";
import { USD_PRECISION } from "@shared/features/onboarding/constants";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { SortingOrder } from "types";
import AnimatedInProgressIcon from "common/Animations/AnimatedInProgressIcon";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import IconWithFallback from "Global/IconWithFallback";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  formatClock,
  formatDay,
  MINUS,
} from "features/bots/BotForm/Backtesting/NewLook/format";

interface ActivitiesTableProps {
  activities?: BacktestingActivity[];
  shouldInitializePageNumber?: boolean;
  inProgress?: boolean;
  /** New look: the activities are still loading (skeleton rows). */
  loading?: boolean;
}

const ActivitiesTable = ({
  activities,
  shouldInitializePageNumber,
  inProgress,
  loading,
}: ActivitiesTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const theme = useTheme();
  const { Trades } = useEmptyState();
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.DESCEND
  );

  const isTradeActivity = (activity: BacktestingActivity) =>
    activity.activity_component === "Trade";
  const isClosedActivity = (activity: BacktestingActivity) =>
    ["Fulfilled", "Partially Fulfilled", "Closed", "Cancelled"].includes(
      activity.status
    );

  const getActivitiesSort = (
    prioritySort?: (a: BacktestingActivity, b: BacktestingActivity) => number
  ) => {
    return (a: BacktestingActivity, b: BacktestingActivity) => {
      const timeSort = sortByNumberProperty(a, b, TableColumns.TIME);
      const isTradeActivitySort = +isTradeActivity(a) - +isTradeActivity(b);
      const isClosedSort = +isClosedActivity(a) - +isClosedActivity(b);
      const fallbackSort = timeSort || isClosedSort || isTradeActivitySort;
      return prioritySort?.(a, b) || fallbackSort;
    };
  };

  const shouldHidePnl = (activity: BacktestingActivity) =>
    !isClosedActivity(activity) || !isTradeActivity(activity);

  const parsedActivities = useMemo(() => {
    return activities
      ?.map((activity) => {
        if (shouldHidePnl(activity))
          return {
            ...activity,
            graph_data: { ...activity.graph_data, profit: undefined },
          };
        return activity;
      })
      .map((activity, index) => ({
        ...activity,
        key: index,
      }));
  }, [activities]);

  const onSortChange: TableProps<BacktestingActivity>["onChange"] = (
    _,
    __,
    sorter
  ) => {
    const order = Array.isArray(sorter) ? sorter[0]?.order : sorter.order;
    setSortDirection(
      order === "ascend" ? SortingOrder.ASCEND : SortingOrder.DESCEND
    );
  };
  const pnlSort = getActivitiesSort((a, b) => {
    const aValue = a.graph_data.profit
      ? (a.graph_data.profit / 100) * a.relevant_amount
      : undefined;
    const bValue = b.graph_data.profit
      ? (b.graph_data.profit / 100) * b.relevant_amount
      : undefined;
    if (aValue === undefined && bValue === undefined) return 0;
    else if (aValue === undefined) return -sortDirection;
    if (bValue === undefined) return sortDirection;
    return sortByNumberProperty(aValue, bValue);
  });

  if (theme.isNewLook) {
    // New look: rows in the platform's roles — the symbol over its execution
    // price, the order over its status, figures right-aligned and tabular,
    // the PnL coloured by direction (no tinted tag).
    const tokens = getNewLookTokens(theme);
    const usd = (value: number) =>
      `${value < 0 ? MINUS : ""}$${getLocaleStringWithPrecision(
        Math.abs(value),
        USD_PRECISION
      )}`;
    const END = { align: "right", className: "nl-end" } as const;
    const newLookColumns: ColumnsType<BacktestingActivity> = [
      {
        title: t("backtesting.labels.symbol"),
        dataIndex: TableColumns.SYMBOL,
        sorter: getActivitiesSort((a, b) =>
          sortByStringProperty(a, b, TableColumns.SYMBOL)
        ),
        showSorterTooltip: false,
        render: (_, activity) => (
          <NLPair>
            <IconWithFallback
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={activity.symbol}
              pxIconSize={24}
              iconStyle={{ marginRight: 0, flex: "none" }}
            />
            <NLLines>
              <NLName>{activity.symbol}</NLName>
              {activity.execution_price > 0 && (
                <NLMeta>
                  {activity.execution_price}{" "}
                  {getPairFromSymbol(activity.symbol)}
                </NLMeta>
              )}
            </NLLines>
          </NLPair>
        ),
      },
      {
        title: t("backtesting.labels.entity"),
        dataIndex: "description",
        showSorterTooltip: false,
        // The trade it belongs to rides on the meta line, so the table fits
        // the form's column without a scrollbar.
        render: (_, activity) => (
          <NLLines>
            <NLText>{activity.description}</NLText>
            <NLMeta>
              {activity.status}
              {activity.trade_number !== undefined &&
                !activity.description?.includes(`#${activity.trade_number}`) &&
                ` · ${t("backtesting.labels.trade_id")} ${
                  activity.trade_number
                }`}
            </NLMeta>
          </NLLines>
        ),
      },
      {
        title: t("backtesting.labels.time"),
        dataIndex: TableColumns.TIME,
        defaultSortOrder: "descend",
        sorter: getActivitiesSort(),
        showSorterTooltip: false,
        render: (time) => (
          <NLLines>
            <NLText>{formatDay(time)}</NLText>
            <NLMeta>{formatClock(time)}</NLMeta>
          </NLLines>
        ),
      },
      {
        title: t("backtesting.labels.amount"),
        dataIndex: TableColumns.AMOUNT,
        sorter: getActivitiesSort((a, b) =>
          sortByNumberProperty(a, b, TableColumns.AMOUNT)
        ),
        showSorterTooltip: false,
        ...END,
        render: (amount) =>
          amount ? <NLFigure>{usd(amount)}</NLFigure> : <NLMeta>—</NLMeta>,
      },
      {
        title: t("backtesting.labels.pnl"),
        dataIndex: TableColumns.PNL,
        sorter: pnlSort,
        showSorterTooltip: false,
        ...END,
        render: (graphData, activity) => {
          if (graphData.profit === undefined) return <NLMeta>—</NLMeta>;
          const value = Number(
            toFixed(
              (graphData.profit / 100) * activity.relevant_amount,
              USD_PRECISION
            )
          );
          return (
            <NLFigure style={{ color: directionColor(tokens, value) }}>
              {value > 0 ? "+" : ""}
              {usd(value)}
            </NLFigure>
          );
        },
      },
    ];
    const showSkeleton = loading && !parsedActivities?.length;
    return (
      <NLSection>
        <NLHeader>
          <NLTitle>{t("backtesting.labels.activities")}</NLTitle>
          {!!parsedActivities?.length && (
            <NLCount>{parsedActivities.length}</NLCount>
          )}
          <HelperTooltip
            description={t("backtesting.helper_tooltips.activities")}
            analyticsPageName={AnalyticsPageName.BACKTESTING}
            analyticsFieldName="Activities"
          />
        </NLHeader>
        <NLTableWrapper>
          <Table<BacktestingActivity>
            columns={newLookColumns}
            dataSource={showSkeleton ? [] : parsedActivities}
            locale={{
              emptyText: showSkeleton ? (
                <NLSkeletonRows aria-busy={true}>
                  {Array.from({ length: 4 }, (_, index) => (
                    <NLSkeletonRow key={index}>
                      <Skeleton width={120} height={12} />
                      <Skeleton width={90} height={12} />
                      <Skeleton width={70} height={12} />
                      <Skeleton width={56} height={12} />
                    </NLSkeletonRow>
                  ))}
                </NLSkeletonRows>
              ) : (
                <PanelEmpty
                  title={
                    inProgress
                      ? t("backtesting.empty_states.waiting_for_activities")
                      : t("backtesting.empty_states.no_activities")
                  }
                />
              ),
            }}
            shouldInitializePageNumber={shouldInitializePageNumber}
            onChange={onSortChange}
          />
        </NLTableWrapper>
      </NLSection>
    );
  }

  let columns: ColumnsType<BacktestingActivity> = [
    {
      title: (
        <div>
          <ColumnTitle>{t("backtesting.labels.symbol")}</ColumnTitle>
          <ColumnSubTitle>
            ({t("backtesting.labels.execution_price")})
          </ColumnSubTitle>
        </div>
      ),
      dataIndex: TableColumns.SYMBOL,
      sorter: getActivitiesSort((a, b) =>
        sortByStringProperty(a, b, TableColumns.SYMBOL)
      ),
      showSorterTooltip: false,
      render: (_, activity) => (
        <TextWithIcon
          iconType={ICON_TYPES.COIN}
          iconSize="xxxl"
          coinOrExchangeName={activity.symbol}
          iconStyle={{ marginRight: 12 }}
          text={
            <div>
              <Title level={4}>{activity.symbol}</Title>
              {activity.execution_price > 0 && (
                <TextWithIcon
                  iconType={ICON_TYPES.COIN}
                  coinOrExchangeName={getPairFromSymbol(activity.symbol)}
                  iconSize="s"
                  text={activity.execution_price}
                  textStyle={{
                    color: theme.colors.secondaryGrey,
                    fontSize: theme.typography.fontSizes.S,
                    lineHeight: "16px",
                  }}
                />
              )}
            </div>
          }
        />
      ),
    },
    {
      title: (
        <div>
          <ColumnTitle>{t("backtesting.labels.entity")}</ColumnTitle>
          <ColumnSubTitle>({t("labels.status")})</ColumnSubTitle>
        </div>
      ),
      dataIndex: "description",
      showSorterTooltip: false,
      render: (_, activity) => (
        <div>
          <Text>{activity.description}</Text>
          <Text type="secondary" $size={typography.fontSizes.S}>
            ({activity.status})
          </Text>
        </div>
      ),
    },
    {
      title: t("backtesting.labels.amount"),
      dataIndex: TableColumns.AMOUNT,
      sorter: getActivitiesSort((a, b) =>
        sortByNumberProperty(a, b, TableColumns.AMOUNT)
      ),
      showSorterTooltip: false,
      render: (amount) =>
        amount ? <Text>${toFixed(amount, USD_PRECISION)}</Text> : "-",
    },
    {
      title: t("backtesting.labels.time"),
      dataIndex: TableColumns.TIME,
      defaultSortOrder: "descend",
      sorter: getActivitiesSort(),
      showSorterTooltip: false,
      render: (time) => (
        <Text>{formatDate(time, true, true, true, false)}</Text>
      ),
    },
    {
      title: t("backtesting.labels.pnl"),
      dataIndex: TableColumns.PNL,
      sorter: getActivitiesSort((a, b) => {
        const aValue = a.graph_data.profit
          ? (a.graph_data.profit / 100) * a.relevant_amount
          : undefined;
        const bValue = b.graph_data.profit
          ? (b.graph_data.profit / 100) * b.relevant_amount
          : undefined;
        if (aValue === undefined && bValue === undefined) return 0;
        else if (aValue === undefined) return -sortDirection;
        if (bValue === undefined) return sortDirection;
        return sortByNumberProperty(aValue, bValue);
      }),
      showSorterTooltip: false,
      render: (graphData, activity) =>
        graphData.profit !== undefined ? (
          <ChangeTag
            value={toFixed(
              (graphData.profit / 100) * activity.relevant_amount,
              USD_PRECISION
            )}
            prefix="$"
            showArrow={false}
            style={{ margin: 0 }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: t("backtesting.labels.trade_id"),
      dataIndex: TableColumns.TRADE_ID,
      sorter: getActivitiesSort((a, b) =>
        sortByNumberProperty(a, b, TableColumns.TRADE_ID)
      ),
      showSorterTooltip: false,
      render: (tradeNumber) => <Text>{tradeNumber}</Text>,
    },
  ];

  const emptyState = (
    <EmptyView
      title={
        inProgress
          ? t("backtesting.empty_states.waiting_for_activities")
          : t("backtesting.empty_states.no_activities")
      }
      icon={inProgress ? <AnimatedInProgressIcon /> : <Trades />}
      iconWidth={inProgress ? 60 : 100}
    />
  );

  return (
    <div style={{ marginTop: 32 }}>
      <Title level={isMobile ? 4 : 3} style={{ margin: "12px 0" }}>
        {t("backtesting.labels.activities")}
        <HelperTooltip
          description={t("backtesting.helper_tooltips.activities")}
          analyticsPageName={AnalyticsPageName.BACKTESTING}
          analyticsFieldName="Activities"
        />
      </Title>
      <Table<BacktestingActivity>
        columns={columns}
        dataSource={parsedActivities}
        locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
        shouldInitializePageNumber={shouldInitializePageNumber}
        onChange={(_, __, sorter: any) => {
          setSortDirection(
            sorter.order === "ascend"
              ? SortingOrder.ASCEND
              : SortingOrder.DESCEND
          );
        }}
      />
    </div>
  );
};

export default ActivitiesTable;

enum TableColumns {
  SYMBOL = "symbol",
  TRADE_ID = "trade_number",
  PNL = "graph_data",
  TIME = "relevant_time",
  AMOUNT = "relevant_amount",
}

const ColumnTitle = styled(Title).attrs({ level: 4 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  color: ${(p) => p.theme.colors.basic};
  ${(p) =>
    p.theme.isNewLook &&
    `
    &.ant-typography {
      font-size: 12.5px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      color: ${p.theme.colors.secondaryText};
    }
  `}
`;
const ColumnSubTitle = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;

/* New look */
const NLSection = styled.section`
  margin-top: 24px;
`;
const NLHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  margin-bottom: 4px;
`;
const NLTitle = styled.h3`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NLCount = styled.span`
  ${newLookType.meta};
  font-weight: 500;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 6px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: center;
`;
const NLPair = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const NLLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NLName = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const NLText = styled.span`
  ${newLookType.body};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const NLFigure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const NLMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const NLSkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
`;
const NLSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 0.8fr;
  align-items: center;
  gap: 24px;
  min-height: 56px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
/** Micro headers, ~56px hairline rows; the edges line up with the content. */
const NLTableWrapper = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    height: 36px;
    padding: 8px 12px;
    ${newLookType.micro};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    background: transparent !important;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    white-space: nowrap;
    &::before {
      display: none;
    }
  }
  .ant-table-wrapper .ant-table-thead th.ant-table-column-has-sorters:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td,
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort {
    height: 56px;
    padding: 8px 12px;
    background: transparent;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  .ant-table-wrapper .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row
    > .ant-table-cell-row-hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:first-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
    padding-left: 0 !important;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:last-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:last-child {
    padding-right: 0 !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder > td {
    height: auto;
    padding: 0 !important;
    border-bottom: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 4px;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-thead th.nl-end .ant-table-column-sorters {
    justify-content: flex-end;
  }
  .ant-table-pagination.ant-pagination {
    margin: 12px 0 0;
  }
`;
